import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useApi } from '../lib/api'
import { Spinner } from '../components/Spinner'
import { Send } from 'lucide-react'

interface DeepLinkOption {
  label: string
  value: string
}

interface DeepLinkPayload {
  recipes: DeepLinkOption[]
  newsletters: DeepLinkOption[]
  static: DeepLinkOption[]
}

interface SendResult {
  campaign: { id: string; title: string }
  sent_count?: number
  failed_count?: number
  total?: number
  send_error?: string
}

const audiences: Array<{ kind: 'all' | 'premium' | 'user_id' | 'tag'; label: string; needsValue: boolean }> = [
  { kind: 'all', label: 'All devices', needsValue: false },
  { kind: 'premium', label: 'Premium subscribers', needsValue: false },
  { kind: 'user_id', label: 'Single user (Clerk ID)', needsValue: true },
  { kind: 'tag', label: 'Tagged segment', needsValue: true },
]

export function PushCompose() {
  const { call } = useApi()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [deepLink, setDeepLink] = useState('')
  const [audienceKind, setAudienceKind] = useState<typeof audiences[number]['kind']>('all')
  const [audienceValue, setAudienceValue] = useState('')
  const [picker, setPicker] = useState<DeepLinkPayload | null>(null)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<SendResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    call<DeepLinkPayload>('/deep-link-options')
      .then((data) => {
        if (!cancelled) setPicker(data)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
    return () => {
      cancelled = true
    }
  }, [call])

  async function handleSend() {
    setError(null)
    setResult(null)
    setSending(true)
    try {
      const res = await call<SendResult>('/push', {
        method: 'POST',
        body: JSON.stringify({
          title,
          body,
          deep_link: deepLink || null,
          audience_kind: audienceKind,
          audience_value:
            audienceKind === 'user_id'
              ? audienceValue
              : audienceKind === 'tag'
                ? audienceValue
                : null,
        }),
      })
      setResult(res)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSending(false)
    }
  }

  const audience = audiences.find((a) => a.kind === audienceKind)!
  const canSend = title.trim() && body.trim() && (!audience.needsValue || audienceValue.trim())

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-marrow-900 dark:text-bone-50">
            Send a push
          </h1>
          <p className="mt-1 text-sm text-marrow-800/70 dark:text-bone-200/70">
            Audited in <code className="rounded bg-bone-100 px-1.5 py-0.5 font-mono text-xs dark:bg-marrow-800">push_campaigns</code>. APNs sandbox host until production flip.
          </p>
        </div>
        <Link to="/push/history" className="text-sm text-rust-600 hover:underline">
          History →
        </Link>
      </header>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {result && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
          Sent <strong>"{result.campaign.title}"</strong> — {result.sent_count ?? 0} delivered
          {typeof result.failed_count === 'number' && result.failed_count > 0
            ? `, ${result.failed_count} failed`
            : ''}
          {typeof result.total === 'number' ? ` (${result.total} targeted)` : ''}.
          {result.send_error && (
            <div className="mt-1 text-xs text-emerald-900/70">
              send-push: {result.send_error}
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={`Title (${title.length}/80)`}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 80))}
            className="input"
            placeholder="A new issue from the Marrow"
          />
        </Field>
        <Field label="Audience">
          <select
            value={audienceKind}
            onChange={(e) => {
              setAudienceKind(e.target.value as typeof audienceKind)
              setAudienceValue('')
            }}
            className="input"
          >
            {audiences.map((a) => (
              <option key={a.kind} value={a.kind}>
                {a.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label={`Body (${body.length}/240)`}>
        <textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, 240))}
          className="input"
          placeholder='"Why your LDL might rise on keto" — open to read.'
        />
      </Field>

      {audience.needsValue && (
        <Field label={audienceKind === 'user_id' ? 'Clerk user ID' : 'Tag value'}>
          <input
            type="text"
            value={audienceValue}
            onChange={(e) => setAudienceValue(e.target.value)}
            className="input font-mono text-sm"
            placeholder={audienceKind === 'user_id' ? 'user_2abc…' : 'lapsed-7d'}
          />
        </Field>
      )}

      <Field label="Deep link">
        <div className="flex gap-2">
          <input
            type="text"
            value={deepLink}
            onChange={(e) => setDeepLink(e.target.value)}
            className="input font-mono text-sm"
            placeholder="marrow://recipe/ribeye-and-eggs (optional)"
          />
          <DeepLinkPicker
            picker={picker}
            onSelect={(v) => setDeepLink(v)}
          />
        </div>
      </Field>

      <div className="flex justify-end border-t border-bone-200 pt-4 dark:border-marrow-700">
        <button
          type="button"
          disabled={!canSend || sending}
          onClick={handleSend}
          className="flex items-center gap-2 rounded-md bg-rust-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-rust-600 disabled:opacity-50"
        >
          <Send size={16} aria-hidden /> {sending ? 'Sending…' : 'Send now'}
        </button>
      </div>
    </div>
  )
}

function DeepLinkPicker({
  picker,
  onSelect,
}: {
  picker: DeepLinkPayload | null
  onSelect: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  if (!picker) return <Spinner size={20} />
  const groups: Array<{ label: string; items: DeepLinkOption[] }> = [
    { label: 'Static', items: picker.static },
    { label: 'Newsletters', items: picker.newsletters },
    { label: 'Recipes', items: picker.recipes },
  ]
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded-md border border-bone-300 bg-white px-3 py-2 text-sm font-medium text-marrow-900 hover:bg-bone-100 dark:border-marrow-700 dark:bg-marrow-800 dark:text-bone-50 dark:hover:bg-marrow-700"
      >
        Pick…
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 max-h-96 w-80 overflow-y-auto rounded-md border border-bone-200 bg-white shadow-lg dark:border-marrow-700 dark:bg-marrow-800">
          {groups.map((g) => (
            <div key={g.label}>
              <div className="border-b border-bone-200 bg-bone-100/40 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-marrow-800/60 dark:border-marrow-700 dark:bg-marrow-700/40 dark:text-bone-200/60">
                {g.label}
              </div>
              {g.items.length === 0 ? (
                <div className="px-3 py-2 text-xs italic text-marrow-800/40 dark:text-bone-200/40">none</div>
              ) : (
                g.items.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => {
                      onSelect(item.value)
                      setOpen(false)
                    }}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-bone-100 dark:hover:bg-marrow-700"
                  >
                    <div className="truncate font-medium text-marrow-900 dark:text-bone-50">{item.label}</div>
                    <div className="truncate font-mono text-xs text-marrow-800/60 dark:text-bone-200/60">{item.value}</div>
                  </button>
                ))
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-marrow-800/60 dark:text-bone-200/60">
        {label}
      </span>
      {children}
    </label>
  )
}
